import { useEffect, useState } from "react";
import { Plus, Trash2, FileText } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PageHeader } from "@/components/portal/widgets";
import api, { inr } from "@/lib/api";
import { toast } from "sonner";

const emptyRow = { name: "", qty: "", unit: "", price: "" };

export default function PurchaseOrders() {
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [notes, setNotes] = useState("");

  const load = () => api.get("/franchise/purchase-orders").then((r) => setPos(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/products?limit=500").then((r) => setSuppliers(r.data.slice(0, 0))).catch(() => {}); }, []);

  const total = rows.reduce((s, r) => s + (parseFloat(r.price) || 0) * (parseInt(r.qty) || 0), 0);

  const submit = async () => {
    const items = rows.filter((r) => r.name && r.qty).map((r) => ({ name: r.name, qty: parseInt(r.qty), unit: r.unit, price: parseFloat(r.price) || 0 }));
    if (items.length === 0) { toast.error("Add at least one item"); return; }
    try {
      await api.post("/franchise/purchase-orders", { store_id: "", items, notes });
      toast.success("Purchase order raised to Head Office");
      setOpen(false); setRows([{ ...emptyRow }]); setNotes(""); load();
    } catch (e) { toast.error("Failed to raise PO"); }
  };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Purchase Orders" subtitle="Raise stock replenishment requests to Head Office."
        action={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button data-testid="new-po-btn" className="bg-yellow-400 hover:bg-yellow-500 text-slate-900"><Plus className="w-4 h-4 mr-1" /> New Purchase Order</Button></DialogTrigger>
            <DialogContent className="max-w-2xl" data-testid="new-po-modal">
              <DialogHeader><DialogTitle className="font-heading">Raise Purchase Order</DialogTitle></DialogHeader>
              <div className="space-y-3">
                {rows.map((r, i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-5"><Label className="text-xs">Item</Label><Input data-testid={`po-item-${i}`} value={r.name} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} /></div>
                    <div className="col-span-2"><Label className="text-xs">Qty</Label><Input data-testid={`po-qty-${i}`} type="number" value={r.qty} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, qty: e.target.value } : x))} /></div>
                    <div className="col-span-2"><Label className="text-xs">Unit</Label><Input value={r.unit} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, unit: e.target.value } : x))} /></div>
                    <div className="col-span-2"><Label className="text-xs">Price</Label><Input type="number" value={r.price} onChange={(e) => setRows(rows.map((x, j) => j === i ? { ...x, price: e.target.value } : x))} /></div>
                    <button className="col-span-1 text-red-500 pb-2" onClick={() => setRows(rows.filter((_, j) => j !== i))}><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setRows([...rows, { ...emptyRow }])} data-testid="po-add-row"><Plus className="w-4 h-4 mr-1" /> Add item</Button>
                <Textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
                <div className="flex justify-between items-center pt-2 border-t"><span className="font-heading font-bold">Estimated Total: {inr(total)}</span>
                  <Button onClick={submit} data-testid="po-submit" className="bg-yellow-400 hover:bg-yellow-500 text-slate-900">Submit PO</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        } />

      <Card className="border-slate-200/80 dark:border-slate-800 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
              <th className="py-3 px-4">Reference</th><th className="py-3 px-4">Items</th><th className="py-3 px-4">Total</th><th className="py-3 px-4">Raised By</th><th className="py-3 px-4">Status</th>
            </tr></thead>
            <tbody>
              {pos.length === 0 && <tr><td colSpan={5} className="py-10 text-center text-muted-foreground">No purchase orders yet. Raise your first one.</td></tr>}
              {pos.map((p) => (
                <tr key={p.id} data-testid={`po-row-${p.id}`} className="border-b border-slate-100 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                  <td className="py-2.5 px-4 font-mono text-xs">{p.reference}</td>
                  <td className="py-2.5 px-4 text-muted-foreground">{p.items?.length} items</td>
                  <td className="py-2.5 px-4 font-semibold">{inr(p.total)}</td>
                  <td className="py-2.5 px-4">{p.raised_by}</td>
                  <td className="py-2.5 px-4"><Badge variant="secondary" className="capitalize">{p.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
